import { room, Video } from "../../libs/roomnew";
import { STREAMING_STATUS } from "../base/tracks/constants";
import { getRemoteParticipantDesktopTrack } from "../base/tracks/functions";
import { requestScreenshareKeyframe } from "../base/video-quality/actions";

const TICK_MS = 2000;

// Frames flat while bytes still arrive => genuine decode freeze; a static screen cannot produce this
// (no bytes), so this window can be short.
const CONFIRM_DECODE_MS = 6000;

// Frames flat AND bytes flat overlaps with a legitimately static screen, so require a longer window
// before treating it as a freeze.
const CONFIRM_NODATA_MS = 12000;

// After a keyframe nudge, wait this long for frames to resume before showing the message / escalating.
const RECOVERY_GRACE_MS = 4000;

// A transport restart renegotiates every remote media session, so never run it more than once per window.
const RESTART_COOLDOWN_MS = 30000;

let watchdogTimer = null;
let lastRestartAt = 0;
const trackState = new Map();

const getSsrc = (kinoTrack) => {
    try {
        return kinoTrack?.getSSRC?.() ?? kinoTrack?.ssrc ?? null;
    } catch (error) {
        return null;
    }
};

const getSourceName = (kinoTrack) => {
    try {
        return kinoTrack?.getSourceName?.() ?? null;
    } catch (error) {
        return null;
    }
};

const isForwardedSource = (sourceName) => {
    try {
        return !!sourceName && room.getForwardedSources().includes(sourceName);
    } catch (error) {
        return false;
    }
};

const findInboundVideo = (reports, ssrc, trackId) => {
    let match = null;
    reports.forEach((report) => {
        if (match) { return; }
        if (report.type !== "inbound-rtp") { return; }
        if (report.kind !== "video" && report.mediaType !== "video") { return; }
        if ((ssrc && report.ssrc === ssrc) || (trackId && report.trackIdentifier === trackId)) {
            match = report;
        }
    });
    return match;
};

const showFrozenMessage = (pid) => {
    const v = Video.videos[pid];
    if (!v) { return; }
    try {
        v.frozenByWatchdog = true;
        v.isVideoPlaying = false;
        v.videoWillMute();
    } catch (error) {
        console.error("[FreezeWatchdog] showFrozenMessage failed", error);
    }
};

const clearFrozenMessage = (pid) => {
    const v = Video.videos[pid];
    if (!v) { return; }
    try {
        v.frozenByWatchdog = false;
        v.isVideoPlaying = true;
        v.onVideoUnmuted();
    } catch (error) {
        console.error("[FreezeWatchdog] clearFrozenMessage failed", error);
    }
};

const inspectParticipant = (pid, reports, now) => {
    const track = getRemoteParticipantDesktopTrack(pid);
    if (!track || !track.kinoTrack) {
        trackState.delete(pid);
        return;
    }
    const sourceName = getSourceName(track.kinoTrack);
    if (!isForwardedSource(sourceName)) {
        trackState.delete(pid);
        return;
    }
    const inbound = findInboundVideo(reports, getSsrc(track.kinoTrack), track.kinoTrack?.track?.id);
    if (!inbound) {
        return;
    }
    const frames = typeof inbound.framesDecoded === "number" ? inbound.framesDecoded : 0;
    const bytes = typeof inbound.bytesReceived === "number" ? inbound.bytesReceived : 0;

    let st = trackState.get(pid);
    if (!st) {
        trackState.set(pid, {
            frames,
            bytes,
            stalledSince: 0,
            phase: "healthy",
            nudgedAt: 0,
            restarted: false,
            messaged: false,
        });
        return;
    }

    const framesAdvanced = frames > st.frames;
    const bytesAdvanced = bytes > st.bytes;
    st.frames = frames;
    st.bytes = bytes;

    if (framesAdvanced) {
        if (st.phase === "recovering" && st.messaged) {
            clearFrozenMessage(pid);
        }
        st.stalledSince = 0;
        st.phase = "healthy";
        st.nudgedAt = 0;
        st.restarted = false;
        st.messaged = false;
        return;
    }

    if (st.phase === "healthy") {
        // Defer to the lib if it already flagged this source; the watchdog only covers the case where
        // the receiver never got a status change (status still ACTIVE).
        if (track.status !== STREAMING_STATUS.ACTIVE) {
            return;
        }
        if (!st.stalledSince) {
            st.stalledSince = now;
            return;
        }
        const confirmMs = bytesAdvanced ? CONFIRM_DECODE_MS : CONFIRM_NODATA_MS;
        if (now - st.stalledSince >= confirmMs) {
            st.phase = "recovering";
            st.nudgedAt = now;
            requestScreenshareKeyframe(sourceName);
        }
        return;
    }

    // st.phase === "recovering"
    const sinceNudge = now - st.nudgedAt;
    if (sinceNudge >= RECOVERY_GRACE_MS) {
        if (!bytesAdvanced
            && !st.restarted
            && (now - lastRestartAt) > RESTART_COOLDOWN_MS
            && typeof room._restartMediaSessions === "function") {
            st.restarted = true;
            lastRestartAt = now;
            try {
                room._restartMediaSessions();
            } catch (error) {
                console.error("[FreezeWatchdog] _restartMediaSessions failed", error);
            }
        }
        if (!st.messaged) {
            st.messaged = true;
            showFrozenMessage(pid);
        }
        if (sinceNudge >= RECOVERY_GRACE_MS * 2) {
            st.nudgedAt = now;
            requestScreenshareKeyframe(sourceName);
        }
    }
};

const tick = async () => {
    try {
        if (!room || typeof room.getActivePeerConnection !== "function") { return; }
        const pc = room.getActivePeerConnection();
        if (!pc || typeof pc.getStats !== "function") { return; }
        let reports;
        try {
            reports = await pc.getStats();
        } catch (error) {
            return;
        }
        const now = Date.now();
        for (const pid in Video.videos) {
            inspectParticipant(pid, reports, now);
        }
        for (const pid of [...trackState.keys()]) {
            if (!Video.videos[pid]) {
                trackState.delete(pid);
            }
        }
    } catch (error) {
        console.error("[FreezeWatchdog] tick failed", error);
    }
};

export const startScreenshareFreezeWatchdog = () => {
    if (watchdogTimer) { return; }
    watchdogTimer = setInterval(tick, TICK_MS);
};

export const stopScreenshareFreezeWatchdog = () => {
    if (watchdogTimer) {
        clearInterval(watchdogTimer);
        watchdogTimer = null;
    }
    trackState.clear();
};
